import { FC, useEffect, useLayoutEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { setAuthRedirectNavigate } from "@/lib/auth/authRedirectNavigate";
import IndexEntryRedirect from "./IndexEntryRedirect";
import AppRoutes from "../constants/routes/AppRoutes";
import SignInView from "../pages/auth/SignInView";
import PageNotFoundView from "../pages/PageNotFoundView";
import LegislationListView from "../pages/legislation/LegislationListView";
import UnAuthorizedView from "../pages/auth/UnAuthorizedView";
import ChatView from "../pages/chat/ChatView";
import WorkspaceView from "../pages/workspace/WorkspaceView";
import WorkspaceDocumentView from "../pages/workspace/WorkspaceDocumentView";
import WorkspaceListView from "@/pages/workspace/WorkspaceListView";
import FilesTab from "@/pages/workspace/partials/workspace-tabs/FilesTab";
import CommentsTab from "@/pages/workspace/partials/workspace-tabs/CommentsTab";
import SettingsTab from "@/pages/workspace/partials/workspace-tabs/SettingsTab";
import PromptsView from "../pages/prompt-library/PromptsView";
import AccessDeniedView from "@/pages/AccessDeniedView";
import InsightsView from "@/pages/tenant-admin/insights/InsightsView";
import { ROUTE_TITLES } from "@/constants/routes/AppRoutes";
import UserConversationOverview from "@/pages/tenant-admin/user-conversation-overview/UserConversationOverview";
import useVersionCheck from "@/hooks/useVersionCheck";
import OnboardingView from "@/pages/onboarding/OnboardingView";
import UsersView from "@/pages/tenant-admin/users/UsersView";
import FeatureView from "@/pages/settings/features/FeatureView";
import { useAuth0 } from "@auth0/auth0-react";
import useUserStore from "@/stores/user.store";
import { useFlags, useUnleashContext } from "@unleash/proxy-client-react";
import useUnleashContextStore from "@/stores/unleash-context.store";
import { getTenantIdFromToken } from "@/utils/auth";
import useSourceFeatureFlags from "@/hooks/useSourceFeatureFlags";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import { setEnabledFeatures } from "@/stores/enabled-features.store";
import useSidebarStore from "@/stores/sidebar.store";
import { useOrganizationSwitch } from "@/hooks/useOrganizationSwitch";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import { useKeyboardShortcut } from "@/utils/shortcut";
import {
  RightSidebarProvider,
  RightSidebarContentProvider,
  SidebarInset,
  SidebarProvider,
} from "@capacit-com/lara-law-shared-ui";
import { AppSidebar } from "@/components/app-sidebar/app-sidebar";
import ReleaseNotesDialog from "@/components/release-notes/ReleaseNotesDialog";
import NotificationSettingsDialog from "@/components/dialogs/NotificationSettingsDialog";
import RightSidebarRenderer from "@/components/content-sidebar/RightSidebarRenderer";
import { AuthenticationGuard } from "./AuthenticationGuard";
import ToneOfVoiceView from "@/pages/settings/tone-of-voice/ToneOfVoiceView";
import MetadataTypesView from "@/pages/settings/metadata/MetadataTypesView";
import { logger } from "@/lib/logger";
import { isInteractiveAuthError } from "@/lib/auth/authRecovery";
import DashboardView from "@/pages/dashboard/DashboardView";
import GlobalSearchView from "@/pages/global-search/GlobalSearchView";
import InsightsNavigationView from "@/pages/tenant-admin/InsightsNavigationView";
import { Domain, getDomain } from "@/utils/domain";
import UserManagement from "@/pages/settings/user-management/UserManagement";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import LegislationViewRouter from "@/pages/legislation/LegislationViewRouter";
import UnifiedLegislationHTMLView from "../pages/legislation/UnifiedLegislationHTMLView";
import UnifiedLegislationPDFView from "../pages/legislation/UnifiedLegislationPDFView";
import WorkspaceDocumentPage from "@/pages/workspace/WorkspaceDocumentPage";
import ExistingUsersTable from "@/pages/settings/user-management/components/ExistingUsersTable";
import GroupsView from "@/pages/settings/user-management/components/GroupsView";
import InvitedUsersTable from "@/pages/settings/user-management/components/InvitedUsersTable";
import OrphanedWorkspacesTable from "@/pages/settings/user-management/components/OrphanedWorkspacesTable";
import WorkspaceDocumentRedirectView from "@/pages/workspace/WorkspaceDocumentRedirectView";
import EntraDirectorySyncView from "@/pages/settings/entra-directory-sync/EntraDirectorySyncView";
import SynonymGroupsPage from "@/pages/settings/synonym-groups/SynonymGroupsPage";
import SynonymGroupDetailPage from "@/pages/settings/synonym-groups/SynonymGroupDetailPage";
import PiiWhitelistSettingsPage from "@/pages/settings/pii-whitelist/PiiWhitelistSettingsPage";
import ConsolidationPrioritySettingsPage from "@/pages/settings/consolidation-priority/ConsolidationPrioritySettingsPage";
import useConsolidationPrioritizationEnabled from "@/hooks/useConsolidationPrioritizationEnabled";
import DocumentLinkRedirectPage from "@/pages/document-link/DocumentLinkRedirectPage";
import WidgetTemplatesView from "@/pages/dashboard/WidgetTemplatesView";
import { forgetWorkspaceReturnOutside } from "@/lib/navigation/workspaceReturn";

const AppRouter: FC = () => {
  useVersionCheck();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const getCachedToken = useCachedAccessToken();
  const { me } = useUserStore();
  const updateContext = useUnleashContext();
  const setUserContextSettled = useUnleashContextStore(
    (state) => state.setUserContextSettled
  );
  const {
    appSidebarVisible,
    isAppSidebarOpen,
    setIsAppSidebarOpen,
    isAppSidebarYielding,
    setAppSidebarYielding,
  } = useSidebarStore();
  // Folded away while a page needs the width; opening it by hand ends that.
  const isAppSidebarShown = isAppSidebarOpen && !isAppSidebarYielding;
  const setAppSidebarShown = (open: boolean) => {
    setAppSidebarYielding(false);
    setIsAppSidebarOpen(open);
  };
  const flags = useFlags();
  const { getEnabledFlagNames } = useSourceFeatureFlags();
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const consolidationPrioritizationEnabled =
    useConsolidationPrioritizationEnabled();

  // Handle organization switching
  useOrganizationSwitch();

  // Wire the left-sidebar keyboard shortcut (Cmd/Ctrl+B) externally so
  // SidebarProvider in shared-ui stays store-free.
  useKeyboardShortcut({
    key: "b",
    callback: () => setAppSidebarShown(!isAppSidebarShown),
  });

  useEffect(() => {
    const getTitle = () => {
      // Check for exact static routes first
      if (ROUTE_TITLES[location.pathname]) {
        return ROUTE_TITLES[location.pathname];
      }

      // For dynamic routes, find the matching pattern
      const pathSegments = location.pathname.split("/");
      const matchingRoute = Object.keys(ROUTE_TITLES).find((route) => {
        const routeSegments = route.split("/");
        if (routeSegments.length !== pathSegments.length) return false;

        return routeSegments.every((segment, index) => {
          // If segment contains ':', it's a dynamic part
          return segment.includes(":") || segment === pathSegments[index];
        });
      });

      return matchingRoute
        ? ROUTE_TITLES[matchingRoute]
        : getDomain(window.location.hostname) === Domain.LODA
          ? "Danske A-kassers Lovportal"
          : "Next-gen AI juridisk bistand";
    };

    document.title =
      getDomain(window.location.hostname) === Domain.LODA
        ? `Loda | ${getTitle()}`
        : `Lara | ${getTitle()}`;
  }, [location.pathname]);

  // This initializes the unleash context with the user ID and tenant ID if the user is not coming from the auth0 login page
  useEffect(() => {
    const fetchUserData = async () => {
      if (isAuthenticated && me) {
        try {
          const tenantId = await getTenantIdFromToken(getCachedToken);
          if (!tenantId) {
            logger.warn("No tenant id in the access token for Unleash", {
              operation: "updateUnleashContext",
            });
            return;
          }
          // updateContext resolves after the proxy refetches with the user
          // context — only then are per-user flag values final.
          await updateContext({ userId: me.id, properties: { tenantId } });
        } catch (error) {
          // A dead Auth0 session is already being sent to an interactive login,
          // which the token getter logs once (LAR-597).
          if (!isInteractiveAuthError(error)) {
            logger.error("Failed to set the Unleash user context", {
              operation: "updateUnleashContext",
              error,
            });
          }
        } finally {
          // Settle even when the context update fails, so one-shot consumers
          // (e.g. the legislation PDF fallback) proceed on the flag values we
          // have instead of waiting forever.
          setUserContextSettled(true);
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, me]);

  // A workspace's back button only lasts while the reader stays inside the workspace.
  useEffect(() => {
    forgetWorkspaceReturnOutside(location.pathname);
  }, [location.pathname]);

  // Sync enabled feature flags to module-level store for the X-Enabled-Features HTTP header
  useEffect(() => {
    setEnabledFeatures(getEnabledFlagNames());
  }, [flags, getEnabledFlagNames]);

  useLayoutEffect(() => {
    setAuthRedirectNavigate(navigate);
    return () => setAuthRedirectNavigate(undefined);
  }, [navigate]);

  /**** DOM ****/
  return (
    <SidebarProvider open={isAppSidebarShown} onOpenChange={setAppSidebarShown}>
      <RightSidebarProvider>
        <RightSidebarContentProvider>
          {appSidebarVisible && (
            <>
              <AppSidebar />
              {/* Beside the sidebar, not inside it: the sidebar remounts across the md breakpoint. */}
              <ReleaseNotesDialog />
              <NotificationSettingsDialog />
            </>
          )}
          <SidebarInset>
            <Routes>
              <Route path={AppRoutes.auth} element={<SignInView />} />
              <Route
                path={AppRoutes.unauthorized}
                element={<UnAuthorizedView />}
              />
              <Route path={AppRoutes.index} element={<IndexEntryRedirect />} />
              <Route
                path={AppRoutes.onboarding}
                element={
                  <AuthenticationGuard>
                    <OnboardingView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.dashboard}
                element={
                  <AuthenticationGuard>
                    <DashboardView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chat}
                element={
                  <AuthenticationGuard>
                    <ChatView />
                  </AuthenticationGuard>
                }
              />
              {/* Same view as /chat; the id lets a reload or re-login reopen the
                  conversation instead of dropping it (LAR-361). Registered after
                  the document routes below purely for readability — React Router
                  ranks the longer, static-prefixed paths first regardless. */}
              <Route
                path={AppRoutes.chatConversation()}
                element={
                  <AuthenticationGuard>
                    <ChatView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chatLegislationPDFView()}
                element={
                  <AuthenticationGuard>
                    <UnifiedLegislationPDFView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chatWorkspacePDFView()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceDocumentView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chatLegislationHTMLView()}
                element={
                  <AuthenticationGuard>
                    <UnifiedLegislationHTMLView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chatWorkspaceHTMLView()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceDocumentPage />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.legislations}
                element={
                  <AuthenticationGuard>
                    <LegislationListView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.legislationHTML()}
                element={
                  <AuthenticationGuard>
                    <LegislationViewRouter />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.legislationPDF()}
                element={
                  <AuthenticationGuard>
                    <UnifiedLegislationPDFView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspaces}
                element={
                  <AuthenticationGuard>
                    <WorkspaceListView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspace()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceView />
                  </AuthenticationGuard>
                }
              >
                <Route
                  path="files"
                  element={
                    <AuthenticationGuard>
                      <FilesTab />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="files/*"
                  element={
                    <AuthenticationGuard>
                      <FilesTab />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="comments"
                  element={
                    <AuthenticationGuard>
                      <CommentsTab />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="comments/:documentId"
                  element={
                    <AuthenticationGuard>
                      <CommentsTab />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <AuthenticationGuard>
                      <SettingsTab />
                    </AuthenticationGuard>
                  }
                />
              </Route>
              <Route
                path={AppRoutes.workspaceDocumentPDF()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceDocumentView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspaceDocumentHTML()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceDocumentPage />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspaceDocumentRedirect()}
                element={
                  <AuthenticationGuard>
                    <WorkspaceDocumentRedirectView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.notFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.promptLibrary}
                element={
                  <AuthenticationGuard>
                    <PermissionGate
                      oneOf={[
                        Permission.CreateTag,
                        Permission.UpdateTag,
                        Permission.DeleteTag,
                        Permission.UpdatePromptsApproval,
                      ]}
                    >
                      <PromptsView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path="/insights"
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.ReadInsights]}>
                      <InsightsNavigationView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              >
                <Route
                  path="conversations"
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ReadInsights]}>
                        <InsightsView />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="users"
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ReadInsights]}>
                        <UsersView />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="user-overview/:conversationId/:isDeleted"
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ReadInsights]}>
                        <UserConversationOverview />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path={AppRoutes.insightsUserOverviewNotFound}
                  element={
                    <AuthenticationGuard>
                      <PageNotFoundView />
                    </AuthenticationGuard>
                  }
                />
              </Route>
              <Route
                path={AppRoutes.settingsToneOfVoice}
                element={
                  <AuthenticationGuard>
                    <PermissionGate
                      oneOf={[
                        Permission.CreateToneOfVoice,
                        Permission.UpdateToneOfVoice,
                        Permission.DeleteToneOfVoice,
                      ]}
                    >
                      <ToneOfVoiceView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsUserManagement}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.ReadRoles]}>
                      <UserManagement />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              >
                <Route
                  path={AppRoutes.settingsUserManagementUsers}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ReadRoles]}>
                        <ExistingUsersTable />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path={AppRoutes.settingsUserManagementInvitedUsers}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ReadRoles]}>
                        <InvitedUsersTable />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path={AppRoutes.settingsUserManagementGroups}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate
                        oneOf={[
                          Permission.CreateFeatureGroups,
                          Permission.UpdateFeatureGroups,
                        ]}
                      >
                        <GroupsView />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path={AppRoutes.settingsUserManagementOrphanedWorkspaces}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate
                        allOf={[Permission.ManageOrphanedWorkspaces]}
                      >
                        <OrphanedWorkspacesTable />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
              </Route>
              <Route
                path={AppRoutes.settingsEntraDirectorySync}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.UpdateTenantMetadata]}>
                      <EntraDirectorySyncView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsFeatures}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.ReadFeatureFlag]}>
                      <FeatureView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsMetadataTypes}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.UpdateTenantMetadata]}>
                      <MetadataTypesView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsSynonyms}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.UpdateSynonymGroup]}>
                      <SynonymGroupsPage />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsSynonymDetail()}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.UpdateSynonymGroup]}>
                      <SynonymGroupDetailPage />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.settingsWidgetTemplates}
                element={
                  <AuthenticationGuard>
                    <PermissionGate allOf={[Permission.ManageWidgetTemplates]}>
                      <WidgetTemplatesView />
                    </PermissionGate>
                  </AuthenticationGuard>
                }
              />
              {/* Registered only while the PII-Redaction flag is on; off → 404. */}
              {piiFeatureEnabled && (
                <Route
                  path={AppRoutes.settingsPiiWhitelist}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate allOf={[Permission.ManagePiiSettings]}>
                        <PiiWhitelistSettingsPage />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
              )}
              {/* Registered only while the Consolidation-Prioritization flag is
                  on (same pattern as the PII whitelist route); off → 404. */}
              {consolidationPrioritizationEnabled && (
                <Route
                  path={AppRoutes.settingsConsolidationPriority}
                  element={
                    <AuthenticationGuard>
                      <PermissionGate
                        allOf={[Permission.ManageConsolidationPriority]}
                      >
                        <ConsolidationPrioritySettingsPage />
                      </PermissionGate>
                    </AuthenticationGuard>
                  }
                />
              )}
              <Route
                path={AppRoutes.tenantSearch}
                element={
                  <AuthenticationGuard>
                    <GlobalSearchView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.documentLinkRedirect()}
                element={
                  <AuthenticationGuard>
                    <DocumentLinkRedirectPage />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspaceFileNotFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.workspaceNotFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.legislationFileNotFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.legislationNotFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.chatFileNotFound}
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path={AppRoutes.accessDenied}
                element={
                  <AuthenticationGuard>
                    <AccessDeniedView />
                  </AuthenticationGuard>
                }
              />
              <Route
                path="*"
                element={
                  <AuthenticationGuard>
                    <PageNotFoundView />
                  </AuthenticationGuard>
                }
              />
            </Routes>
          </SidebarInset>
          <RightSidebarRenderer />
        </RightSidebarContentProvider>
      </RightSidebarProvider>
    </SidebarProvider>
  );
};

export default AppRouter;
